import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api';

interface FDDetails {
  id?: string;
  botclient_id?: string;
  name?: string;
  botclient_name?: string;
  total_interactions: number;
  total_raw_interactions?: number;
  passthrough_interactions?: number;
  olympus_interactions?: number;
  message_interactions?: number;
  tool_call_interactions?: number;
  crm_sync_interactions?: number;
  passive_stt_interactions?: number;
  intro_audio_interactions?: number;
  brain_job_interactions?: number;
  other_interactions?: number;
  total_tool_calls?: number;
  unique_chats?: number;
  models?: string[];
  interaction_type_breakdown?: Record<string, number>;
}

interface PeriodInfo {
  start: string | null;
  end: string | null;
  is_current: boolean;
}

// Cache estático para os dados históricos
// Como bo_interacao_botclient nunca tem update nem delete, podemos armazenar em cache
let cachedFDData: FDDetails[] | null = null;
let cachedPeriodInfo: PeriodInfo | null = null;
let lastCacheTime: number = 0;
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 horas em milissegundos

const NUMERIC_FIELDS: Array<keyof FDDetails> = [
  'total_interactions',
  'total_raw_interactions',
  'passthrough_interactions',
  'olympus_interactions',
  'message_interactions',
  'tool_call_interactions',
  'crm_sync_interactions',
  'passive_stt_interactions',
  'intro_audio_interactions',
  'brain_job_interactions',
  'other_interactions',
  'total_tool_calls',
  'unique_chats',
];

const normalizeFDDetails = (item: any): FDDetails => {
  const normalized = { ...item };
  NUMERIC_FIELDS.forEach(field => {
    normalized[field] = Number(normalized[field]) || 0;
  });
  normalized.models = Array.isArray(normalized.models) ? normalized.models : [];
  normalized.interaction_type_breakdown = normalized.interaction_type_breakdown || {};
  return normalized as FDDetails;
};

const csvValue = (value: unknown): string => {
  const text = String(value ?? '');
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const FDRankingPanel: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [fdData, setFDData] = useState<FDDetails[]>([]);
  const [periodInfo, setPeriodInfo] = useState<PeriodInfo | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(20);
  
  // Sorting states
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Date filter states
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [useCustomDates, setUseCustomDates] = useState<boolean>(false);
  
  // Export states
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportSuccess, setExportSuccess] = useState<boolean>(false);

  // Formatar período para exibição
  const formatPeriod = (period: PeriodInfo | null): string => {
    if (!period || !period.start) return 'Período desconhecido';
    
    const startDate = new Date(period.start);
    const endDate = new Date(period.end || period.start);
    
    // Se for período customizado (mais de um mês ou não alinhado com mês), mostrar datas
    if (useCustomDates || (period as any).is_custom) {
      const startFormatted = startDate.toLocaleDateString('pt-BR');
      const endFormatted = endDate.toLocaleDateString('pt-BR');
      return `${startFormatted} a ${endFormatted}`;
    }
    
    // Se for mês completo, mostrar só o mês e ano
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    
    return `${monthNames[startDate.getMonth()]} de ${startDate.getFullYear()}`;
  };

  // Função para buscar dados (com cache apenas para dados do mês atual)
  const fetchData = async (customStartDate?: string, customEndDate?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      // Se for período customizado, não usar cache
      if (customStartDate && customEndDate) {
        console.log(`Buscando dados para período customizado: ${customStartDate} até ${customEndDate}`);
        const response = await api.getAllFDsForMonth(customStartDate, customEndDate);
        
        if (!response.success) {
          throw new Error(response.message || 'Falha ao buscar dados');
        }
        
        const data = response.data || [];
        const period = response.period || null;
        
        console.log('FD ranking data (período customizado):', data.length, 'records loaded');
        console.log('Period info:', period);
        
        // Ensure total_interactions is a number for all items
        const processedData = data.map(normalizeFDDetails);
        
        setFDData(processedData);
        setPeriodInfo(period);
        setLastUpdated(new Date());
        return;
      }
      
      // Para dados do mês atual, verificar cache
      const now = Date.now();
      if (cachedFDData && cachedPeriodInfo && now - lastCacheTime < CACHE_TTL) {
        console.log('Usando dados em cache para FDs:', cachedFDData.length, 'registros');
        setFDData(cachedFDData);
        setPeriodInfo(cachedPeriodInfo);
        setLoading(false);
        return;
      }
      
      // Fetch data from API - sem limitação para obter todos os FDs
      const response = await api.getAllFDsForMonth();
      
      if (!response.success) {
        throw new Error(response.message || 'Falha ao buscar dados');
      }
      
      const data = response.data || [];
      const period = response.period || null;
      
      console.log('FD ranking data:', data.length, 'records loaded');
      console.log('Period info:', period);
      
      // Ensure total_interactions is a number for all items
      const processedData = data.map(normalizeFDDetails);
      
      // Atualizar o cache
      cachedFDData = processedData;
      cachedPeriodInfo = period;
      lastCacheTime = now;
      
      setFDData(processedData);
      setPeriodInfo(period);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Erro ao buscar ranking de FDs:', err);
      setError('Erro ao carregar dados de ranking de FDs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Refresh data a cada 12 horas se não estiver usando datas customizadas
    if (!useCustomDates) {
      const interval = setInterval(() => fetchData(), 12 * 60 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [useCustomDates]);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Get the name for display
  const getName = (item: FDDetails): string => {
    return item.name || item.botclient_name || 
      (item.id || item.botclient_id || '').substring(0, 8) + '...';
  };
  
  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setCurrentPage(1);
  };
  
  // Filter and sort the data
  const filteredAndSorted = useMemo(() => {
    // First filter by search query
    const filtered = searchQuery.trim() === '' 
      ? fdData 
      : fdData.filter(item => {
          const name = getName(item).toLowerCase();
          return name.includes(searchQuery.toLowerCase());
        });
    
    // Then sort the data
    return [...filtered].sort((a, b) => {
      // Sort by interactions
      if (a.total_interactions !== b.total_interactions) {
        return sortDirection === 'asc' 
          ? a.total_interactions - b.total_interactions
          : b.total_interactions - a.total_interactions;
      }
      
      // For equal interactions, sort by name
      const nameA = getName(a).toLowerCase();
      const nameB = getName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [fdData, sortDirection, searchQuery]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAndSorted.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAndSorted.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  
  // Handle page size change
  const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };
  
  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  // Forçar atualização dos dados (ignorando cache)
  const handleForceRefresh = async () => {
    if (useCustomDates && startDate && endDate) {
      await fetchData(startDate, endDate);
    } else {
      // Reset cache e buscar dados do mês atual
      cachedFDData = null;
      cachedPeriodInfo = null;
      lastCacheTime = 0;
      await fetchData();
    }
  };
  
  // Aplicar filtro de data customizado
  const handleApplyDateFilter = async () => {
    if (!startDate || !endDate) {
      setError('Por favor, selecione ambas as datas (início e fim)');
      return;
    }
    
    if (new Date(startDate) > new Date(endDate)) {
      setError('A data de início deve ser anterior à data de fim');
      return;
    }
    
    setUseCustomDates(true);
    await fetchData(startDate, endDate);
  };
  
  // Resetar para dados do mês atual
  const handleResetToCurrentMonth = async () => {
    setUseCustomDates(false);
    setStartDate('');
    setEndDate('');
    await fetchData();
  };
  
  // Exportar dados para CSV
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      
      // Usar os dados filtrados e ordenados
      const dataToExport = filteredAndSorted;
      
      if (dataToExport.length === 0) {
        setError('Não há dados para exportar');
        return;
      }
      
      // Criar cabeçalho CSV
      const csvHeaders = [
        'Posição',
        'Nome do FD',
        'Total Bruto',
        'Interações Contabilizadas',
        'Passthrough Excluído',
        'Olympus',
        'Message',
        'Tool Call',
        'CRM Sync',
        'Passive STT',
        'Intro Audio',
        'Brain Job',
        'Outros Tipos',
        'Tool Calls',
        'Chats',
        'Modelos'
      ];
      
      // Criar linhas CSV
      const csvRows = dataToExport.map((item, index) => {
        const position = index + 1;
        const name = getName(item);
        return [
          position,
          csvValue(name),
          item.total_raw_interactions || item.total_interactions,
          item.total_interactions,
          item.passthrough_interactions || 0,
          item.olympus_interactions || 0,
          item.message_interactions || 0,
          item.tool_call_interactions || 0,
          item.crm_sync_interactions || 0,
          item.passive_stt_interactions || 0,
          item.intro_audio_interactions || 0,
          item.brain_job_interactions || 0,
          item.other_interactions || 0,
          item.total_tool_calls || 0,
          item.unique_chats || 0,
          csvValue((item.models || []).join('|'))
        ];
      });
      
      // Juntar tudo
      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.join(','))
        .join('\n');
      
      // Criar e baixar arquivo
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        
        // Nome do arquivo com período
        const periodText = periodInfo && periodInfo.start && periodInfo.end 
          ? `${periodInfo.start}_a_${periodInfo.end}` 
          : 'periodo_atual';
        link.setAttribute('download', `ranking_fds_${periodText}.csv`);
        
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 2000);
      
    } catch (err) {
      console.error('Erro ao exportar CSV:', err);
      setError('Erro ao exportar dados para CSV');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="ranking-panel card mt-8">
        <h3 className="section-title with-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Ranking de FDs por Interações
        </h3>
        <div className="p-4 flex items-center justify-center">
          <div className="animate-pulse text-center">
            <div className="h-4 w-32 bg-gray-700 rounded mx-auto"></div>
            <div className="mt-2 h-3 w-24 bg-gray-700 rounded mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ranking-panel card mt-8">
        <h3 className="section-title with-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Ranking de FDs por Interações
        </h3>
        <div className="p-4 text-center text-red-400">
          <p>Erro ao carregar ranking de FDs. Tente novamente mais tarde.</p>
          {error && <p className="text-sm mt-2">{error}</p>}
        </div>
      </div>
    );
  }
  
  // Calculate how many results are filtered out
  const filteredCount = filteredAndSorted.length;
  const totalCount = fdData.length;
  const isFiltering = searchQuery.trim() !== '';
  
  // Formatar o título com o mês atual
  const periodText = formatPeriod(periodInfo);
  const panelTitle = `Ranking de FDs por Interações - ${periodText}`;
  const totalCountedInteractions = fdData.reduce((sum, item) => sum + item.total_interactions, 0);
  const totalRawInteractions = fdData.reduce(
    (sum, item) => sum + (item.total_raw_interactions || item.total_interactions),
    0
  );
  const totalPassthroughInteractions = fdData.reduce(
    (sum, item) => sum + (item.passthrough_interactions || 0),
    0
  );
  
  return (
    <div className="ranking-panel card mt-8">
      <h3 className="section-title with-icon flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        {panelTitle}
        <div className="ml-auto flex items-center text-xs text-gray-400">
          <span className="mr-3">
            {cachedFDData ? 'Dados em cache' : 'Dados atualizados'} • {lastUpdated.toLocaleTimeString()}
          </span>
          <button 
            onClick={handleForceRefresh}
            className="text-gray-400 hover:text-gray-200 transition-colors"
            title="Atualizar dados"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </h3>
      
      {/* Date filter controls */}
      <div className="flex flex-col gap-3 my-3 px-3 border-b border-gray-700 pb-3">
        <div className="flex flex-col sm:flex-row gap-2 items-center">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-gray-400">Data início:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              title="Selecionar data de início"
              className="bg-gray-800 text-gray-200 text-sm border border-gray-700 rounded px-2 py-1"
            />
            
            <label className="text-xs text-gray-400">Data fim:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              title="Selecionar data de fim"
              className="bg-gray-800 text-gray-200 text-sm border border-gray-700 rounded px-2 py-1"
            />
            
            <button
              onClick={handleApplyDateFilter}
              disabled={!startDate || !endDate || loading}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-3 py-1 rounded transition-colors"
            >
              Aplicar
            </button>
            
            {useCustomDates && (
              <button
                onClick={handleResetToCurrentMonth}
                disabled={loading}
                className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-3 py-1 rounded transition-colors"
              >
                Resetar
              </button>
            )}
          </div>
          
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={handleExportCSV}
              disabled={exporting || fdData.length === 0}
              className={`${
                exportSuccess 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-purple-600 hover:bg-purple-700'
              } disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-xs px-3 py-1 rounded transition-colors flex items-center gap-1`}
            >
              {exporting ? (
                <>
                  <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Exportando...
                </>
              ) : exportSuccess ? (
                '✓ Exportado!'
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Exportar CSV
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Search and filter controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-2 my-3 px-3">
        <div className="w-full sm:w-auto relative">
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="bg-gray-800 text-gray-200 text-sm border border-gray-700 rounded px-3 py-2 pl-9 w-full"
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 absolute left-3 top-2.5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-200"
              title="Limpar busca"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Itens por página:</span>
          <select 
            className="bg-gray-800 text-gray-200 text-xs border border-gray-700 rounded px-2 py-1"
            value={itemsPerPage}
            onChange={handlePageSizeChange}
            title="Selecionar número de itens por página"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={250}>250</option>
            <option value={500}>500</option>
          </select>
        </div>
      </div>
      
      {/* Filter results message */}
      {isFiltering && (
        <div className="px-3 text-xs text-gray-400 mt-1 mb-2">
          {filteredCount === 0 ? (
            <span className="text-yellow-400">Nenhum resultado encontrado</span>
          ) : (
            <span>
              Mostrando {filteredCount} de {totalCount} FDs{searchQuery ? ` para "${searchQuery}"` : ''}
            </span>
          )}
        </div>
      )}
      
      {/* Statistics */}
      <div className="px-3 text-xs text-gray-400 mt-1 mb-2 flex justify-between">
        <div>
          <span className="font-medium">{totalCount}</span> FDs no total • 
          <span className="font-medium ml-1">{fdData.filter(item => item.total_interactions > 0).length}</span> com interações
        </div>
        <div>
          <span className="font-medium">
            {new Intl.NumberFormat('pt-BR').format(totalCountedInteractions)}
          </span> contabilizadas • 
          <span className="font-medium ml-1">
            {new Intl.NumberFormat('pt-BR').format(totalRawInteractions)}
          </span> bruto • 
          <span className="font-medium ml-1">
            {new Intl.NumberFormat('pt-BR').format(totalPassthroughInteractions)}
          </span> passthrough removidas
        </div>
      </div>
      
      {/* Table with ranking */}
      <div className="overflow-x-auto mt-1">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-gray-800">
              <th className="py-2 px-3 w-12 text-center">#</th>
              <th className="py-2 px-3">FD</th>
              <th className="py-2 px-3 text-right">Bruto</th>
              <th className="py-2 px-3 text-right">Passthrough</th>
              <th 
                className="py-2 px-3 text-right cursor-pointer select-none" 
                onClick={toggleSortDirection}
              >
                <div className="flex items-center justify-end">
                  Interações
                  <span className="ml-1">
                    {sortDirection === 'asc' ? '▲' : '▼'}
                  </span>
                </div>
              </th>
              <th className="py-2 px-3 text-right">Message</th>
              <th className="py-2 px-3 text-right">Tool Call</th>
              <th className="py-2 px-3 text-right">CRM</th>
              <th className="py-2 px-3 text-right">Audio/STT</th>
              <th className="py-2 px-3 text-right">Outros</th>
              <th className="py-2 px-3 text-right">Tool Calls</th>
              <th className="py-2 px-3 text-right">Chats</th>
              <th className="py-2 px-3">Modelos</th>
              <th className="py-2 px-3 w-1/4">Distribuição</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.length === 0 ? (
              <tr>
                <td colSpan={14} className="py-4 text-center text-gray-400">
                  {isFiltering 
                    ? `Nenhum FD encontrado${searchQuery ? ` para "${searchQuery}"` : ''}`
                    : 'Nenhum dado disponível'}
                </td>
              </tr>
            ) : currentItems.length === 0 ? (
              <tr>
                <td colSpan={14} className="py-4 text-center text-gray-400">
                  Carregando dados...
                </td>
              </tr>
            ) : (
              currentItems.map((item, index) => {
                // Calculate the ranking position considering pagination
                const rankPosition = indexOfFirstItem + index + 1;
                
                // Calculate the percentage for the distribution bar
                const maxInteractions = filteredAndSorted[0]?.total_interactions || 1;
                const percentage = maxInteractions > 0 ? (item.total_interactions / maxInteractions) * 100 : 0;
                
                // Determine if this item has zero interactions
                const hasNoInteractions = item.total_interactions === 0;
                
                return (
                  <tr key={index} className={`border-b border-gray-700 hover:bg-gray-750 ${hasNoInteractions ? 'opacity-70' : ''}`}>
                    <td className="py-2 px-3 text-center font-medium">
                      {rankPosition}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center">
                        <span className={`font-medium ${hasNoInteractions ? 'text-gray-400' : 'text-gray-200'}`}>
                          {getName(item)}
                        </span>
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right text-gray-300">
                      {new Intl.NumberFormat('pt-BR').format(
                        Number(item.total_raw_interactions || item.total_interactions)
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {item.passthrough_interactions && item.passthrough_interactions > 0
                        ? <span className="text-amber-300">{new Intl.NumberFormat('pt-BR').format(item.passthrough_interactions)}</span>
                        : <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right font-medium">
                      {new Intl.NumberFormat('pt-BR').format(
                        Number(item.total_interactions)
                      )}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-300">
                      {item.message_interactions && item.message_interactions > 0
                        ? new Intl.NumberFormat('pt-BR').format(item.message_interactions)
                        : <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-300">
                      {item.tool_call_interactions && item.tool_call_interactions > 0
                        ? new Intl.NumberFormat('pt-BR').format(item.tool_call_interactions)
                        : <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-300">
                      {item.crm_sync_interactions && item.crm_sync_interactions > 0
                        ? new Intl.NumberFormat('pt-BR').format(item.crm_sync_interactions)
                        : <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-300">
                      {item.passive_stt_interactions && item.passive_stt_interactions > 0
                        ? new Intl.NumberFormat('pt-BR').format(item.passive_stt_interactions)
                        : <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-300">
                      {((item.intro_audio_interactions || 0) + (item.brain_job_interactions || 0) + (item.other_interactions || 0)) > 0
                        ? new Intl.NumberFormat('pt-BR').format((item.intro_audio_interactions || 0) + (item.brain_job_interactions || 0) + (item.other_interactions || 0))
                        : <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-300">
                      {item.total_tool_calls && item.total_tool_calls > 0
                        ? new Intl.NumberFormat('pt-BR').format(item.total_tool_calls)
                        : <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-300">
                      {item.unique_chats && item.unique_chats > 0
                        ? new Intl.NumberFormat('pt-BR').format(item.unique_chats)
                        : <span className="text-gray-500">—</span>}
                    </td>
                    <td className="py-2 px-3">
                      {item.models && item.models.length > 0
                        ? <span className="inline-flex flex-wrap gap-1">{item.models.slice(0, 3).map(m => (
                            <span key={m} className="px-1.5 py-0.5 text-xs rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">{m}</span>
                          ))}</span>
                        : <span className="text-gray-500 text-xs">—</span>}
                    </td>
                    <td className="py-2 px-3">
                      {hasNoInteractions ? (
                        <div className="text-xs text-gray-500 italic">Sem interações</div>
                      ) : (
                        <div className="w-full bg-gray-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${
                              rankPosition <= 3 ? (
                                rankPosition === 1 ? 'bg-gradient-to-r from-yellow-500 to-orange-600' : 
                                rankPosition === 2 ? 'bg-gradient-to-r from-orange-500 to-amber-600' :
                                'bg-gradient-to-r from-amber-500 to-yellow-600'
                              ) : 'bg-gray-500'
                            }`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      {filteredAndSorted.length > 0 && (
        <div className="mt-4 px-3 flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="text-xs text-gray-400 order-2 sm:order-1">
            Mostrando {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredAndSorted.length)} de {filteredAndSorted.length} FDs
            {isFiltering && ` (filtrado de ${totalCount})`}
          </div>
          
          <div className="flex order-1 sm:order-2">
            <button 
              onClick={() => paginate(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-2 py-1 mx-1 rounded text-xs ${currentPage === 1 ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
            >
              Anterior
            </button>
            
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              // Calculate page numbers to show (centered around current page)
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button 
                  key={i}
                  onClick={() => paginate(pageNum)}
                  className={`w-7 h-7 mx-1 rounded-full text-xs flex items-center justify-center ${pageNum === currentPage ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button 
              onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-2 py-1 mx-1 rounded text-xs ${currentPage === totalPages ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'}`}
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FDRankingPanel; 
